import { Anchor } from '@kluein/klue-ui';
import { CheckCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAuth } from 'contexts/auth';
import { useNotifier, NotifierType } from 'hooks/use-notifier';

import type { CompanyFavoriteType } from '../Company.types';

const useCompanyFavorite = (
  {
    company,
    showUndo,
  }: { company?: CompanyFavoriteType; showUndo?: boolean } = {
    showUndo: false,
  },
) => {
  const { isCompanyFavoritesEnabled, user } = useAuth();
  const { notify } = useNotifier();
  const { t } = useTranslation(['Common', 'Company']);
  const dispatch = useDispatch();

  const userCompanyFavorites = user?.userData.favoritedCompanies ?? [];
  const [isFavorite, setIsFavorite] = useState(
    userCompanyFavorites.some((favorite) => favorite.id === company?.id),
  );

  const handleFavorite = () => {
    if (!company) {
      throw new Error(
        'Tried to call `handleFavorite` without providing a `company`',
      );
    }
    const { id, name, domain } = company;
    const currentCompany = { id, name, domain };
    if (isFavorite) {
      dispatch.auth.updateUserFavoritedCompanies(
        userCompanyFavorites.filter(
          (favorite) => favorite.id !== currentCompany.id,
        ),
      );
      setIsFavorite(false);
      notify({
        type: NotifierType.INFO,
        message: t('Common:actions.unfavorited'),
        icon: <CheckCircle />,
        children: showUndo ? (
          <Anchor
            size="large"
            onClick={() => {
              dispatch.auth.updateUserFavoritedCompanies(userCompanyFavorites);
              setIsFavorite(true);
            }}
          >
            {t('Common:actions.undo')}
          </Anchor>
        ) : undefined,
      });
    } else {
      dispatch.auth.updateUserFavoritedCompanies([
        currentCompany,
        ...userCompanyFavorites,
      ]);
      setIsFavorite(true);
      notify({
        type: NotifierType.SUCCESS,
        message: t('Common:actions.favorited'),
        icon: <CheckCircle />,
      });
    }
  };

  return {
    isFavorite,
    handleFavorite,
    isCompanyFavoritesEnabled,
    userCompanyFavorites,
  };
};

export default useCompanyFavorite;
